import "./ClientRegistrationCertificationAndConsentComponent.scss";
import {Field, FieldProps, Form, Formik, FormikHelpers} from "formik";
import React, {useCallback, useEffect, useState} from "react";
import FormDebuggerComponent from "../../../shared/components/form-debugger/FormDebuggerComponent";
import FormControlLabelComponent from "../../../shared/components/form-control-label/FormControlLabelComponent";
import FormikInputComponent from "../../../shared/components/form-controls/formik-input/FormikInputComponent";
import FormikDatePickerComponent
    from "../../../shared/components/form-controls/formik-date-picker/FormikDatePickerComponent";
import FormikCheckBoxComponent from "../../../shared/components/form-controls/formik-check-box/FormikCheckBoxComponent";
import _ from "lodash";
import ErrorComponent from "../../../shared/components/error/ErrorComponent";
import ButtonComponent from "../../../shared/components/button/ButtonComponent";
import {ImageConfig, Misc} from "../../../constants";
import * as Yup from "yup";
import {IClientRegistrationConsentDetails} from "../../../shared/models/client.model";
import {CommonService} from "../../../shared/services";
import {IAPIResponseType} from "../../../shared/models/api.model";
import {useDispatch, useSelector} from "react-redux";
import {IRootReducerState} from "../../../store/reducers";
import ESignApprovalComponent from "../../../shared/components/e-sign-approval/ESignApprovalComponent";
import LinkComponent from "../../../shared/components/link/LinkComponent";
import SignaturePopupComponent from "../../../shared/components/signature-popup/SignaturePopupComponent";
import ModalComponent from "../../../shared/components/modal/ModalComponent";
import {getClientSignatureDetails} from "../../../store/actions/client.action";

interface ClientRegistrationCertificationAndConsentComponentProps {
    onNext?: () => void;
    initialValues?: any;
    formId?: string;
    mode?: string;
    appointmentId: any;
    onSubmitComplete?: Function;
}

const registrationCertificationAndConsentFormValidationSchema = Yup.object({
    client_name: Yup.string().required('Name is required'),
    date: Yup.string().nullable(),
    is_terms_acknowledged: Yup.boolean().oneOf([true], 'Please acknowledge to the consent'),
    is_authorized_representative: Yup.boolean(),
    representative_name: Yup.string().when("is_authorized_representative", {
        is: true,
        then: Yup.string().required('Authorized Representative Name is required')
    }),
    signature: Yup.string().required()
});

const RegistrationCertificationAndConsentFormInitialValues: any = {
    client_name: "",
    date: new Date(),
    is_terms_acknowledged: false,
    is_authorized_representative: false,
    representative_name: "",
    signature: "",
    is_certification_and_consent_form_filled: true,
    is_onboarded_certification: true,
    appointment_id: ''
}

const ClientRegistrationCertificationAndConsentComponent = (props: ClientRegistrationCertificationAndConsentComponentProps) => {
    const {onNext, onSubmitComplete, mode, initialValues, formId, appointmentId} = props;
    const [registrationFormInitialValues, setRegistrationFormInitialValues] = useState<IClientRegistrationConsentDetails>(_.cloneDeep(RegistrationCertificationAndConsentFormInitialValues));
    const [isSignaturePopupOpen, setIsSignaturePopupOpen] = useState<boolean>(false);
    const dispatch = useDispatch();
    const {currentUser} = useSelector((state: IRootReducerState) => state.account);
    const formikRef = React.useRef<any>(null);
    const {clientSignatureDetails} = useSelector((state: IRootReducerState) => state.client);


    const onSubmit = useCallback((values: any, {setSubmitting, setErrors}: FormikHelpers<any>) => {
        setSubmitting(true);
        const payload = _.cloneDeep(values);
        payload.appointment_id = appointmentId ? appointmentId : currentUser?.appointment_id;
        payload.date = CommonService.convertDateFormat(payload.date, "MM-DD-YYYY");
        payload.is_onboard_consent_form_filled = true;
        if (!formId) {
            payload.is_onboarded_certification = true;
        } else {
            payload.is_onboarded_certification = false;
        }
        if (!formId) {
            CommonService._client.SubmitRegistrationConsentAPICall(payload)
                .then((response: IAPIResponseType<any>) => {
                    CommonService._alert.showToast(response[Misc.API_RESPONSE_MESSAGE_KEY], "success");
                    setSubmitting(false);
                    onNext && onNext();
                    // navigate(CommonService._routeConfig.ClientInjurySuccess('client-registration'));

                })
                .catch((error: any) => {
                    CommonService.handleErrors(setErrors, error);
                    setSubmitting(false);
                })
        } else {
            CommonService._client.RegistrationConsentUpdateAPICall(payload, formId)
                .then((response: IAPIResponseType<any>) => {
                    CommonService._alert.showToast(response[Misc.API_RESPONSE_MESSAGE_KEY], "success");
                    setSubmitting(false);
                    onSubmitComplete && onSubmitComplete(values);
                    // navigate(CommonService._routeConfig.ClientInjurySuccess('client-registration'));
                })
                .catch((error: any) => {
                    CommonService.handleErrors(setErrors, error);
                    setSubmitting(false);
                })
        }
    }, [onNext, formId, currentUser, onSubmitComplete, appointmentId]);

    // const handleNavigateBack = useCallback(() => {
    //     navigate('/registration?step=medical-history-questionnaire&subStep=musculoskeletal-history');
    // }, [navigate]);

    useEffect(() => {
        dispatch(getClientSignatureDetails(true, appointmentId));
    }, [dispatch, appointmentId]);


    useEffect(() => {
        if (initialValues) {
            setRegistrationFormInitialValues(initialValues)
        }
    }, [initialValues])

    const handleSignaturePopupClose = useCallback(() => {
        setIsSignaturePopupOpen(false);
    }, []);


    useEffect(() => {
        if (currentUser) {
            setRegistrationFormInitialValues((prevValues) => {
                const newInitialValues = {
                    ...prevValues,
                    client_name: `${currentUser.first_name} ${currentUser.last_name}`,
                    is_terms_acknowledged: formikRef.current?.values?.is_terms_acknowledged,
                    is_authorized_representative: formikRef.current?.values?.is_authorized_representative,
                    signature: formikRef.current?.values?.signature
                };

                if (clientSignatureDetails) {
                    newInitialValues.is_terms_acknowledged = clientSignatureDetails.is_terms_acknowledged;
                    newInitialValues.representative_name = clientSignatureDetails.representative_name;
                    newInitialValues.is_authorized_representative = clientSignatureDetails.is_authorized_representative;
                    newInitialValues.signature = clientSignatureDetails.signature;
                }

                return newInitialValues;
            });
        }
    }, [currentUser, clientSignatureDetails, formikRef]);


    return (
        <div className={'client-registration-certification-and-consent-component client-registration-details-block'}>
            <div className="client-registration-details-block__header">
                <div className="client-registration-details-block__title">
                    Certification and Consent
                </div>
            </div>
            <Formik
                validationSchema={registrationCertificationAndConsentFormValidationSchema}
                initialValues={registrationFormInitialValues}
                validateOnChange={false}
                validateOnBlur={true}
                innerRef={formikRef}
                enableReinitialize={true}
                validateOnMount={true}
                onSubmit={onSubmit}
            >
                {({values, validateForm, touched, errors, isValid, isSubmitting, setFieldValue}) => {
                    // eslint-disable-next-line react-hooks/rules-of-hooks
                    useEffect(() => {
                        validateForm();
                    }, [validateForm, values]);
                    return (
                        <Form className="t-form" noValidate={true}>
                            <FormDebuggerComponent values={values} errors={errors} showDebugger={false}/>
                            <div className="t-form-controls">
                                <div className="privacy-policy-modal__content_right__block__content">
                                    I hereby certify that I have completed this form to the best of my ability and I
                                    acknowledge that all the information that I have
                                    supplied on this form is true, accurate, current, and complete.<br/><br/>

                                    I understand that my medical history is a very important factor in the direction of
                                    my
                                    care. Medical or physical conditions which are known to me, but which I do not
                                    disclose
                                    on this form or to my provider, may result in serious injury to me. If any of the
                                    above
                                    conditions change, I will immediately inform Kinergy Sports Medicine and Performance
                                    or
                                    my provider. I knowingly and willingly
                                    assume all risks of injury resulting from my failure to disclose accurate, complete
                                    and
                                    updated information in accordance with the above questionnaire.<br/><br/>

                                    I hereby consent to and authorize Kinergy Sports Medicine and Performance to perform
                                    any
                                    medical assessments and treatments deemed necessary for my medical care. I
                                    understand
                                    and am informed that, as in the practice of medicine, rehabilitation therapy may
                                    have
                                    some risks. I understand that I have the right to ask about these risks and have any
                                    questions about my conditions answered prior to treatment. I understand that at any
                                    time, except retrospectively, I may revoke this consent. I acknowledge that no
                                    guarantee
                                    or assurance has been made to me as to the results of the care I will
                                    receive.<br/><br/>
                                </div>
                                <div className="ts-row">
                                    <div className="ts-col-12">
                                        <Field name={'is_terms_acknowledged'} className="t-form-control">
                                            {
                                                (field: FieldProps) => (
                                                    <FormikCheckBoxComponent
                                                        label={<>I acknowledge that I have read and agree to the
                                                            statement as written above.</>}
                                                        required={true}
                                                        formikField={field}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </div>
                                </div>
                                <div className="ts-row">
                                    <div className="ts-col-12">
                                        <Field name={'is_authorized_representative'}
                                               className="t-form-control">
                                            {
                                                (field: FieldProps) => (
                                                    <FormikCheckBoxComponent
                                                        label={'I am an Authorized Representative for the client (ie. Parent, Legal Guardian, Agent, Manager, etc.)'}
                                                        formikField={field}
                                                        onChange={() => {
                                                            setFieldValue('representative_name', '');
                                                        }}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </div>
                                </div>
                                <div className="ts-row">
                                    <div className="ts-col-md-12 ts-col-lg-6">
                                        <Field name={'client_name'} className="t-form-control">
                                            {
                                                (field: FieldProps) => (
                                                    <FormikInputComponent
                                                        id={"name_input"}
                                                        label={'Client Name'}
                                                        placeholder={'Enter your name'}
                                                        required={true}
                                                        disabled={true}
                                                        formikField={field}
                                                        fullWidth={true}
                                                        titleCase={true}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </div>
                                    <div className="ts-col-md-12 ts-col-lg-6">
                                        <Field name={'date'} className="t-form-control">
                                            {
                                                (field: FieldProps) => (
                                                    <FormikDatePickerComponent
                                                        id={"dob_input"}
                                                        label={'Date'}
                                                        placeholder={'Enter Date'}
                                                        formikField={field}
                                                        fullWidth={true}
                                                        disabled={true}
                                                        readOnly={true}
                                                        // clearable={false}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </div>
                                </div>

                                {
                                    values?.is_authorized_representative && <div className="ts-row">
                                        <div className="ts-col-12">
                                            <Field name={'representative_name'} className="t-form-control">
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            label={'Authorized Representative Name'}
                                                            placeholder={'Enter Representative Name'}
                                                            required={true}
                                                            formikField={field}
                                                            fullWidth={true}
                                                            titleCase={true}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </div>
                                    </div>
                                }
                                {!(values?.signature && registrationFormInitialValues?.signature) &&
                                    <div className="ts-row">
                                        <div className="ts-col-12">
                                            <FormControlLabelComponent
                                                className={"font-weight-thin"}
                                                label={"Signature of Client or Authorized Representative"}
                                                required={true}/>
                                            <ButtonComponent
                                                className={'add-signature-button'}
                                                onClick={() => {
                                                    setIsSignaturePopupOpen(true);
                                                }}
                                                prefixIcon={<ImageConfig.AddCircleFilled/>}
                                                variant={'link'}
                                                color={'info'}
                                                size={'medium'}
                                            >
                                                Add Signature
                                            </ButtonComponent>
                                        </div>
                                        {
                                            (_.get(touched, "signature") && !!(_.get(errors, "signature"))) &&
                                            <ErrorComponent
                                                errorText={(_.get(errors, "signature"))}/>
                                        }
                                    </div>
                                }
                                {(values?.signature && registrationFormInitialValues?.signature) &&
                                    <>
                                        <ESignApprovalComponent isSigned={true}
                                                                signature_url={values?.signature}
                                        />
                                        <div className={'signature-remove-button'}>
                                            <LinkComponent onClick={() => {
                                                setFieldValue('signature', "");
                                            }
                                            }>
                                                Remove Signature
                                            </LinkComponent>
                                        </div>
                                    </>
                                }
                                <div className="t-form-actions">
                                    <ButtonComponent
                                        id={"submit_btn"}
                                        type={"submit"}
                                        size={'large'}
                                        prefixIcon={<ImageConfig.ArrowRightCircle/>}
                                        // fullWidth={true}
                                        disabled={!isValid || isSubmitting}
                                        isLoading={isSubmitting}
                                    >
                                        {!formId && mode !== 'add' ? "Next" : "Submit"}
                                    </ButtonComponent>
                                </div>
                            </div>
                        </Form>
                    )
                }}
            </Formik>
            <ModalComponent isOpen={isSignaturePopupOpen}
                            onClose={handleSignaturePopupClose}
                            size={"xl"}
            >
                <SignaturePopupComponent
                    onSign={(signImage: any) => {
                        formikRef?.current.setFieldValue('signature', signImage)
                        setRegistrationFormInitialValues({
                            ...formikRef?.current?.values,
                            signature: signImage
                        })
                        handleSignaturePopupClose();
                    }}
                    handleSignaturePopupClose={handleSignaturePopupClose}
                />
            </ModalComponent>
        </div>
    );

};

export default ClientRegistrationCertificationAndConsentComponent;
